
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import { Options, Vue } from "vue-class-component";
import { initialName, dateToTime } from "@/app/infrastructures/misc/Utils";
import Pill from "@/app/ui/components/pill/index.vue";
import { NotificationController } from "@/app/ui/controllers/NotificationController";
import InfiniteScroll from "@/app/ui/components/infinite-scroll/index.vue";
import { AccountController } from "../../controllers/AccountController";
import { GTMNotificationEvent } from "@/app/infrastructures/misc/gtm-event/custom-events/notification-events";
import { NotificationMetaLogin } from "@/domain/entities/Notification";
import { MainAppController } from "../../controllers/MainAppController";
import { DeviceAuthReviewRequest } from "@/data/payload/api/AccountRequest";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { FcsPermissionManagementLoginApproval } from "@/feature-flags/flags-management-login-approval";
import formatDate from "@/app/infrastructures/misc/common-library/v2/FormatDate";

type ActionType = "approve" | "reject";

@Options({
  components: {
    InfiniteScroll,
    Pill
  }
})
class Detail extends Vue {
  get isCollapse() {
    return AccountController.isCollapseSidebar;
  }

  created() {
    this.getCurrentRoute();
    this.getUnreadList();
    this.getApiData();
    this.getMainNotifications();
  }

  // Header
  getCurrentRoute() {
    return this.$t(this.$route.name as string);
  }

  //   Search
  searchValue = "";
  _onGetSearch(value: string) {
    this.searchValue = value;
    NotificationController._getListNotificationSearch({
      page: 1,
      limit: 10,
      status: "",
      search: value,
      isPinned: 0,
      sort: this.filterValue,
      cache: true
    });
  }
  onSearch = debounce((value) => {
    this._onGetSearch(value);
  }, 500);

  onClearSearch() {
    this.searchValue = "";
    this._onGetSearch("");
  }

  // Type
  filterValue = "desc";
  get filterData() {
    return [
      {
        name: this.$t("notification.filterNewest"),
        value: "desc"
      },
      {
        name: this.$t("notification.filterOldest"),
        value: "asc"
      }
    ];
  }
  filterSelect = false;
  onOpenFilterSelect() {
    this.filterSelect = true;
  }
  onCloseFilterSelect() {
    this.filterSelect = false;
  }
  onSelectFilter(name: string, value: string) {
    this.onCloseFilterSelect();
    this.filterValue = value;
    NotificationController._getListNotification({
      page: 1,
      limit: 10,
      status: "",
      search: this.searchValue,
      isPinned: 0,
      sort: value,
      cache: true
    });
  }

  // Get List
  getUnreadList() {
    NotificationController._getUnreadData(true);
  }

  getMainNotifications() {
    NotificationController._getAnnouncementData({
      page: 1,
      limit: 0,
      status: "",
      search: this.searchValue,
      isPinned: 0,
      sort: this.filterValue,
      cache: true
    });
  }

  get mainNotifications() {
    return NotificationController.primaryNotificationData;
  }

  // Tooltip Notification
  isOpenNotification = false;
  notification = {
    height: 0,
    width: 0,
    top: 0,
    left: 0
  };
  initialNameNotification(value: string | number) {
    const nameValue = String(value || "");
    const initialValue = initialName(nameValue);
    if (!initialValue) {
      return {
        initial: "",
        color: ""
      };
    }
    return initialValue;
  }
  dateToTime(value: string) {
    return dateToTime(value);
  }
  formatDate(date: string) {
    return formatDate(date);
  }
  getSharedNewsSnippet(
    createdBy: string | number,
    title: string,
    maxLength: number,
    useWideEllipsis = false
  ) {
    const creatorName = String(createdBy || "");
    const sharePrefix = ` ${this.$t("notification.sharedNewsPrefix")} `;
    const fullText = creatorName + sharePrefix + title;

    if (creatorName.length + title.length > maxLength) {
      const slicedText = fullText.substr(
        creatorName.length,
        maxLength - creatorName.length
      );
      return `${slicedText}${useWideEllipsis ? " . . . . ." : "..."}`;
    }

    return `${sharePrefix}${title.charAt(0).toUpperCase() + title.slice(1)}`;
  }
  goDetail(id: number, isDeviceMaster = false) {
    if (isDeviceMaster) return;
    
    NotificationController._onReadNotification(id);
    GTMNotificationEvent("notification_display_detail", {
      notificationId: id
    });
    this.$router.push(`/notifications/${id}`);
  }

  // Get infinite scroll
  limit = 10;
  getApiData() {
    NotificationController._getListNotification({
      page: 1,
      limit: this.limit,
      status: "",
      search: this.searchValue,
      isPinned: 0,
      sort: this.filterValue,
      cache: true
    });
    this.limit += 10;
  }

  get isNotificationsExist() {
    const data = NotificationController.groupAllNotification;
    return !isEmpty(data);
  }

  get allNotifications() {
    return NotificationController.groupAllNotification;
  }

  get isLoading() {
    return NotificationController.isLoading;
  }

  // Handle Error
  get isError() {
    return NotificationController.isError;
  }
  get errorCause() {
    return NotificationController.errorCause;
  }
  onReload() {
    this.getUnreadList();
    this.getApiData();
    this.getMainNotifications();
  }

  async onHandleModalAction(
    meta: NotificationMetaLogin,
    actionType: ActionType
  ) {
    MainAppController.showLoading();
    MainAppController.closeMessageModal();

    const action = actionType.toLowerCase() as ActionType;
    const payload = new DeviceAuthReviewRequest(
      meta.deviceApprovalId,
      action,
      meta.deviceIdApproval
    );

    try {
      await AccountController.deviceAuthReview(payload);
      this.showActionToast(meta.deviceAccountName, action);
      this.getApiData();
    } catch (error) {
      this.handleFailedConfirmation(error);
    } finally {
      MainAppController.closeLoading();
    }
  }

  private showActionToast(accountName: string, action: ActionType) {
    const isApprove = action.toLowerCase() === "approve";
    const actionTitle = isApprove
      ? this.$t("notification.actionAccepted")
      : this.$t("notification.actionRejected");
    const actionVerb = isApprove
      ? this.$t("notification.actionAcceptVerb")
      : this.$t("notification.actionRejectVerb");

    const message = `
      <div style="color: ${isApprove ? "#00875A" : "#E40101"}">
        <b>${actionTitle}</b><br />
        ${this.$t("notification.loginRequestHandled", {
          accountName,
          action: actionVerb
        })}
      </div>
    `;
      
    MainAppController.setSnackbarMessage(message);
    MainAppController.setSnackbarPositivity(true);
    MainAppController.setSnackbarTimeout(5000);
    MainAppController.setSnackbarBackgroundColor(
      isApprove ? "#E3FCEF" : "#FEE8E8"
    );
    MainAppController.setSnackbarIconLeft(
      isApprove ? "success-status" : "failed-status"
    );
    MainAppController.setSnackbar(true);
    this.getApiData();
  }

  handleFailedConfirmation(error: any) {
    const errorMessageId = error?.response?.data?.message?.id || "";
    const msg = errorMessageId.toLowerCase();

    const isAccepted = msg.includes("diterima");
    const isRejected = msg.includes("ditolak");

    if (isAccepted || isRejected) {
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          title: msg,
          textSuccess: this.$t("notification.okUpper"),
          onSubmit: () => {
            MainAppController.closeMessageModal();
            this.getApiData();
          },
          image: isAccepted ? "badge-confirmation-general" : "login-failed",
          isButtonFull: true
        })
      );
      return;
    } else {
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          image: "new-badge-failed",
          title: this.$t("notification.failedProcessTitle"),
          message: errorMessageId || error,
          textSuccess: this.$t("notification.tryAgain"),
          textCancel: this.$t("notification.back"),
          onClose: () => {
            this.getApiData();
            MainAppController.closeMessageModal();
          },
          onSubmit: () => {
            MainAppController.closeMessageModal();
            this.getApiData();
          }
        })
      );
    }
  }

  handleConfirmation(meta: NotificationMetaLogin, actionType: ActionType) {
    const actionLabel =
      actionType.toLowerCase() === "approve"
        ? this.$t("notification.accept")
        : this.$t("notification.reject");

    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-confirmation-general",
        title:
          actionType.toLowerCase() === "reject"
            ? this.$t("notification.confirmRejectTitle")
            : this.$t("notification.confirmAcceptTitle"),
        message:
          actionType.toLowerCase() === "reject"
            ? this.$t("notification.confirmRejectMessage")
            : this.$t("notification.confirmAcceptMessage"),
        onSubmit: () => this.onHandleModalAction(meta, actionType),
        onClose: () => {
          MainAppController.closeMessageModal();
        },
        textSuccess: this.$t("notification.confirmActionButton", {
          action: actionLabel
        }),
        textCancel: this.$t("notification.cancel"),
        customWidth: "417px",
        customButton: "w-full",
        customParentButton: "w-full",
        customWrapperButton: "w-full px-6"
      })
    );
  }

  get permissionButtonActionReject() {
    return FcsPermissionManagementLoginApproval.permission_action_reject_enable.isEnabled();
  }

  get permissionButtonActionApprove() {
    return FcsPermissionManagementLoginApproval.permission_action_approve_enable.isEnabled();
  }
}
export default Detail;
